import { isDefined, isNil } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { FEATURE_FLAG_DEFAULT_VALUES } from "@src/appV2/FeatureFlags/constants";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
/* eslint-disable no-restricted-imports */
import type { CognitoAuthentication } from "@src/lib/cognito/src/lib/amplifyAuth";
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import { getFirebaseSingleton } from "@src/lib/firebase/src";
/* eslint-enable no-restricted-imports */
import type { User } from "firebase/auth";
import { useEffect, useRef } from "react";

const COGNITO_SYNC_SOURCE = "useCognitoAuthSync";

/**
 * Hook that synchronizes Firebase authentication with Cognito.
 * Listens to Firebase ID token changes and automatically authenticates with Cognito
 * using the FIREBASE_TOKEN_EXCHANGE custom challenge.
 *
 * - When Firebase user signs in: authenticate with Cognito using Firebase ID token
 * - When Firebase user signs out: sign out from Cognito
 * - Errors in Cognito sync do not block Firebase authentication
 */
export function useCognitoAuthSync(): void {
  const isCognitoSyncEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_COGNITO_AUTH_SYNC, {
    defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.ENABLE_COGNITO_AUTH_SYNC],
  });

  const isSyncingRef = useRef(false);
  const lastSyncedUserIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!isCognitoSyncEnabled) {
      return undefined;
    }

    const firebaseAuth = getFirebaseSingleton();
    const cognitoAuth = getCognitoSingleton();

    const unsubscribe = firebaseAuth.onIdTokenChanged(
      async (firebaseUser: User | null) => {
        if (isNil(firebaseUser)) {
          await handleCognitoSignOut({
            cognitoAuth,
            lastSyncedUserId: lastSyncedUserIdRef.current,
          });
          lastSyncedUserIdRef.current = undefined;
          return;
        }

        if (isSyncingRef.current || lastSyncedUserIdRef.current === firebaseUser.uid) {
          return;
        }

        try {
          isSyncingRef.current = true;
          await signInToCognitoWithFirebaseToken({ cognitoAuth, user: firebaseUser });
          lastSyncedUserIdRef.current = firebaseUser.uid;
        } catch (error: unknown) {
          logCognitoSyncError({
            eventName: APP_V2_APP_EVENTS.COGNITO_FIREBASE_SYNC_ERROR,
            error,
            additionalMetadata: {
              firebaseUserId: firebaseUser.uid,
            },
          });
        } finally {
          isSyncingRef.current = false;
        }
      },
      (error: unknown) => {
        logCognitoSyncError({
          eventName: APP_V2_APP_EVENTS.FIREBASE_ID_TOKEN_CHANGED_ERROR,
          error,
        });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [isCognitoSyncEnabled]);
}

interface LogCognitoSyncErrorRequest {
  eventName: string;
  error: unknown;
  additionalMetadata?: Record<string, unknown>;
}

function logCognitoSyncError(request: LogCognitoSyncErrorRequest): void {
  const { eventName, error, additionalMetadata = {} } = request;

  logError(eventName, {
    error,
    metadata: {
      source: COGNITO_SYNC_SOURCE,
      ...additionalMetadata,
    },
  });
}

interface HandleCognitoSignOutRequest {
  cognitoAuth: CognitoAuthentication;
  lastSyncedUserId: string | undefined;
}

async function handleCognitoSignOut(request: HandleCognitoSignOutRequest): Promise<void> {
  const { cognitoAuth, lastSyncedUserId } = request;

  if (isNil(lastSyncedUserId)) {
    return;
  }

  try {
    await cognitoAuth.signOut();
    logEvent(APP_V2_APP_EVENTS.COGNITO_SIGN_OUT_SUCCESS, {
      source: COGNITO_SYNC_SOURCE,
    });
  } catch (error: unknown) {
    logCognitoSyncError({
      eventName: APP_V2_APP_EVENTS.COGNITO_SIGN_OUT_ERROR,
      error,
    });
  }
}

interface SignInToCognitoWithFirebaseTokenRequest {
  cognitoAuth: CognitoAuthentication;
  user: User;
}

async function signInToCognitoWithFirebaseToken(
  request: SignInToCognitoWithFirebaseTokenRequest
): Promise<void> {
  const { cognitoAuth, user } = request;

  const userCredentials = getUserCredentials(user);

  const shouldSkipSync = await isUserSignedInToCognito({ cognitoAuth, userCredentials });
  if (shouldSkipSync) {
    return;
  }

  const firebaseIdToken = await user.getIdToken();
  const userIdentifier = userCredentials.phoneNumber ?? userCredentials.email;

  if (isNil(userIdentifier)) {
    throw new Error("User has no email or phone number");
  }

  await cognitoAuth.signInWithFirebaseToken({ userIdentifier, firebaseIdToken });
  logEvent(APP_V2_APP_EVENTS.COGNITO_FIREBASE_SYNC_SUCCESS, {
    source: COGNITO_SYNC_SOURCE,
    userIdentifier,
  });
}

interface UserCredentials {
  email: string | undefined;
  phoneNumber: string | undefined;
}

function getUserCredentials(user: User): UserCredentials {
  return {
    email: isDefined(user.email) ? user.email : undefined,
    phoneNumber: isDefined(user.phoneNumber) ? user.phoneNumber : undefined,
  };
}

interface IsUserSignedInToCognitoRequest {
  cognitoAuth: CognitoAuthentication;
  userCredentials: UserCredentials;
}

async function isUserSignedInToCognito(request: IsUserSignedInToCognitoRequest): Promise<boolean> {
  const { cognitoAuth, userCredentials } = request;

  const currentCognitoUser = await cognitoAuth.getCurrentUserInfo();
  if (isNil(currentCognitoUser)) {
    return false;
  }

  const isEmailMatch =
    isDefined(userCredentials.email) &&
    isDefined(currentCognitoUser.email) &&
    userCredentials.email.trim().toLowerCase() === currentCognitoUser.email.trim().toLowerCase();

  const isPhoneMatch =
    isDefined(userCredentials.phoneNumber) &&
    isDefined(currentCognitoUser.phoneNumber) &&
    userCredentials.phoneNumber.trim() === currentCognitoUser.phoneNumber.trim();

  return isEmailMatch || isPhoneMatch;
}
